/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { Hub } from 'interfaces/Home';
import PageHeader from 'components/PageHeader';
import { SectionVariant } from 'interfaces/Section';
import type { AdditionalRanking, EduAdWigetsByPosition, EduRanking } from 'interfaces/EduRanking';
import { useMemo } from 'react';
import type { InnerSectionConfig } from 'api/MicrositeConfig/getMicrositeConfig';
import { Microsites, SectionTitles } from 'constants/constants';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import EDUCATION_ADS_TEST_IDS from 'constants/testsIds/educationAds';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import dynamic from 'next/dynamic';
import * as S from './SubsectionPageStyles';

export interface SectionProps {
  section: Hub;
  rankings?: EduRanking[];
  additionalRankings: AdditionalRanking[];
  adWidgets: EduAdWigetsByPosition;
  childRankingCategories: InnerSectionConfig['childRankingCategories'];
  pagination: PagePagination;
}

const RankingListsTabs = dynamic(() => import('components/education/EduRanking/RankingListsTabs'));
const PaidPlacement = dynamic(() => import('components/PaidPlacement'));
const RankingCardList = dynamic(() => import('components/education/EduRanking/RankingCardList'));
const ChildRankingsSection = dynamic(() => import('components/education/EduRanking/ChildRankingsSection'));
const LatestArticles = dynamic(() => import('components/recommends/ArticleSection/LatestArticles'));

const EduSubsectionPage = ({
  section,
  rankings = [],
  additionalRankings,
  adWidgets,
  childRankingCategories,
  pagination,
}: SectionProps) => {
  const rankingList = useMemo(() => [...rankings, ...additionalRankings], [additionalRankings, rankings]);

  const childRankings = useMemo(() => {
    try {
      const childRankingsAll = rankings.flatMap((ranking) => ranking.childRankings || []);

      if (childRankingsAll.length === 0) return { best: [], more: [] };

      if (!childRankingCategories || childRankingCategories.length === 0) return { best: [], more: childRankingsAll };

      const categories = childRankingCategories.map((category) => category.type);
      const childRankingsInCategories = childRankingsAll.filter((ranking) => categories.includes(ranking.childType));
      const childRankingsNoCategories = childRankingsAll.filter((ranking) => !categories.includes(ranking.childType));

      const best: EduRanking['childRankings'] = [];
      const more: EduRanking['childRankings'] = [];

      childRankingCategories.forEach(({ type }) => {
        const childRankingsList = childRankingsInCategories.filter((ranking) => ranking.childType === type);
        best.push(...childRankingsList.slice(0, 4));
        more.push(...childRankingsList.slice(4));
      });

      more.push(...childRankingsNoCategories);

      return { best, more };
    } catch {
      return { best: [], more: [] };
    }
  }, [childRankingCategories, rankings]);

  const isFirstPage = pagination.currentPage === 1;

  return (
    <S.Wrapper>
      <PageHeader
        className='page-header'
        title={section.title}
        description={section.description}
        breadcrumbs={section.breadcrumbs}
        pageInfo={pagination.pageInfo}
      />
      {isFirstPage && (
        <>
          {rankings && (
            <RankingListsTabs
              tabs={rankings
                .filter((ranking) => !ranking.hideRanking)
                .map(({ slug: listSlug, latestList, title }) => ({
                  cta: {
                    title: 'SEE FULL RANKING',
                    url: listSlug,
                  },
                  items: latestList,
                  title,
                }))}
            />
          )}
          {rankingList.length > 0 && (
            <RankingCardList
              rankings={rankingList}
              variant='rankings-list-category-page'
            >
              {adWidgets && adWidgets.header && adWidgets.header[0] && (
                <S.AdContainer>
                  <PaidPlacement
                    dataCy={EDUCATION_ADS_TEST_IDS.RANKINGS_HUB_HEADER}
                    adContent={adWidgets.header[0].content}
                    adTag={adWidgets.header[0].adTag}
                    position='header'
                    utmCampaign='rankingshub'
                    entityId={section.prefixedDatabaseId}
                  />
                </S.AdContainer>
              )}
            </RankingCardList>
          )}
          {adWidgets && adWidgets.in_stream && adWidgets.in_stream[0] && (
            <S.AdContainer>
              <PaidPlacement
                dataCy={EDUCATION_ADS_TEST_IDS.RANKINGS_HUB_INSTREAM_1}
                adContent={adWidgets.in_stream[0].content}
                adTag={adWidgets.in_stream[0].adTag}
                position='in_stream'
                utmCampaign='rankingshub'
                entityId={section.prefixedDatabaseId}
              />
            </S.AdContainer>
          )}
          {childRankings.best.length > 0 && (
            <ChildRankingsSection
              sectionTitle='Best Programs'
              variant='best-programs-category-page'
              childRankings={childRankings.best}
              types={childRankingCategories}
            />
          )}
        </>
      )}
      <ErrorBoundary
        fragment={UIFragments.LATEST_ARTICLES}
        disabled={!isFirstPage}
      >
        {section.posts && section.posts.length > 0 && (
          <LatestArticles
            pagination={pagination}
            title={SectionTitles.latestArticles}
            sectionTitle={section.title}
            variant={SectionVariant.LatestArticles}
            platform={Microsites.Education}
            pinnedLinks={section.children}
            trackingData={{
              articleNameCD: section.title,
              contentPlacementCD: `${section.name} grid - row`,
              eventCategory: 'navigation',
            }}
            sectionType={section.sectionType}
            articles={section.posts}
          />
        )}
      </ErrorBoundary>
      {isFirstPage && (
        <>
          {childRankings.more.length > 0 && (
            <ChildRankingsSection
              sectionTitle='More Best Rankings'
              variant='more-best-rankings'
              childRankings={childRankings.more}
            />
          )}
          {adWidgets && adWidgets.in_stream && adWidgets.in_stream[1] && (
            <S.AdContainer>
              <PaidPlacement
                dataCy={EDUCATION_ADS_TEST_IDS.RANKINGS_HUB_INSTREAM_2}
                adContent={adWidgets.in_stream[1].content}
                adTag={adWidgets.in_stream[1].adTag}
                position='in_stream'
                utmCampaign='rankingshub'
                entityId={section.prefixedDatabaseId}
              />
            </S.AdContainer>
          )}
        </>
      )}
    </S.Wrapper>
  );
};

export default EduSubsectionPage;
